import { memo } from 'react'

/**
 * Get background color based on section hierarchy
 * Three-layer system:
 * - Layer 1 (Outside section): Page background with purple tint
 * - Layer 2 (Inside section): Section box with purple-tinted background
 * - Layer 3 (Inside inputs): Pure white inputs that stand out
 */
const getSectionBackground = (sectionId, isPrivate) => {
  if (isPrivate) {
    return {
      backgroundColor: 'rgba(249, 250, 251, 0.4)',
      border: '1px dashed rgba(139, 92, 246, 0.25)',
      borderRadius: '12px',
      padding: '24px',
    }
  }

  // Define hierarchy levels with purple-tinted backgrounds
  const hierarchyMap = {
    // Level 1 - Lightest purple tint (Patient info, history)
    'patient-details': { bg: 'rgba(250, 245, 255, 0.5)', border: 'rgba(139, 92, 246, 0.12)' },
    'therapy-history': { bg: 'rgba(250, 245, 255, 0.5)', border: 'rgba(139, 92, 246, 0.12)' },
    'consultation-history': { bg: 'rgba(250, 245, 255, 0.5)', border: 'rgba(139, 92, 246, 0.12)' },
    
    // Level 2 - Light purple tint (Therapy types)
    'type-of-therapy': { bg: 'rgba(243, 232, 255, 0.6)', border: 'rgba(139, 92, 246, 0.16)' },
    'additional-therapy': { bg: 'rgba(243, 232, 255, 0.6)', border: 'rgba(139, 92, 246, 0.16)' },
    
    // Level 3 - Medium purple tint (Observations, Complaints)
    'observations': { bg: 'rgba(237, 233, 254, 0.7)', border: 'rgba(139, 92, 246, 0.2)' },
    'complaints': { bg: 'rgba(237, 233, 254, 0.7)', border: 'rgba(139, 92, 246, 0.2)' },
    
    // Level 4 - Medium-dark purple tint (Diagnosis)
    'diagnosis': { bg: 'rgba(221, 214, 254, 0.75)', border: 'rgba(139, 92, 246, 0.24)' },
    'primary-clinical-diagnosis': { bg: 'rgba(221, 214, 254, 0.75)', border: 'rgba(139, 92, 246, 0.24)' },
    'differential-clinical-diagnoses': { bg: 'rgba(221, 214, 254, 0.75)', border: 'rgba(139, 92, 246, 0.24)' },
    
    // Level 5 - Darker purple tint (Advice, Notes)
    'advice': { bg: 'rgba(196, 181, 253, 0.8)', border: 'rgba(139, 92, 246, 0.28)' },
    'free-form-report': { bg: 'rgba(196, 181, 253, 0.8)', border: 'rgba(139, 92, 246, 0.28)' },
    
    // Level 6 - Darkest purple tint (Next Visit)
    'next-visit': { bg: 'rgba(167, 139, 250, 0.85)', border: 'rgba(139, 92, 246, 0.32)' },
  }

  const hierarchy = hierarchyMap[sectionId] || { bg: 'rgba(237, 233, 254, 0.7)', border: 'rgba(139, 92, 246, 0.2)' }

  return {
    backgroundColor: hierarchy.bg,
    border: `1px solid ${hierarchy.border}`,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(139, 92, 246, 0.08)',
  }
}

/**
 * Wrapper component that conditionally renders sections based on settings
 * Modernized with consistent spacing and visual separation
 */
const SectionWrapper = memo(({ sectionId, isVisible, children, isPrivate = false }) => {
  if (!isVisible) {
    return null
  }

  const sectionStyle = getSectionBackground(sectionId, isPrivate)

  return (
    <div
      data-section-id={sectionId}
      style={{
        width: '100%',
        display: 'block',
        marginBottom: isPrivate ? '0px' : '32px',
        paddingTop: isPrivate ? '48px' : '0px',
        borderTop: isPrivate ? '2px solid rgba(156, 163, 175, 0.3)' : 'none',
        marginTop: isPrivate ? '48px' : '0px',
        ...sectionStyle,
      }}
    >
      {children}
    </div>
  )
})

SectionWrapper.displayName = 'SectionWrapper'

export default SectionWrapper

